'use client';

import { useState } from 'react';

import { cn } from '@/utils/cn';

import { Loader2 } from 'lucide-react';

interface AuthLoadingProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function AuthLoading({
  message = 'Loading...',
  size = 'md',
  className
}: AuthLoadingProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center space-y-4',
        className
      )}>
      <Loader2
        className={cn('animate-spin text-indigo-600', sizeClasses[size])}
      />
      {message && (
        <p className='text-muted-foreground animate-pulse text-sm'>{message}</p>
      )}
    </div>
  );
}

interface AuthLoadingOverlayProps {
  message?: string;
  children?: React.ReactNode;
}

export function AuthLoadingOverlay({
  message = 'Processing...',
  children
}: AuthLoadingOverlayProps) {
  return (
    <div className='relative'>
      {children}
      <div className='bg-background/80 absolute inset-0 flex items-center justify-center rounded-lg backdrop-blur-sm'>
        <AuthLoading message={message} />
      </div>
    </div>
  );
}

// Hook for managing loading states
export function useAuthLoading() {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const setLoading = (key: string, loading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: loading }));
  };

  const isLoading = (key: string) => loadingStates[key] || false;

  const anyLoading = Object.values(loadingStates).some(Boolean);

  return { setLoading, isLoading, anyLoading };
}
